import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Calculator, 
  CheckCircle, 
  Building2, 
  Users, 
  Star, 
  Shield, 
  Target, 
  ArrowRight,
  Calendar,
  MapPin,
  Award,
  Zap,
  TrendingUp,
  Globe2,
  FileText,
  PieChart,
  DollarSign,
  Receipt,
  BarChart3,
  CreditCard,
  Clock
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import '@/styles/animations.css';

const BookKeeping = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const features = [
    {
      title: "Financial Records",
      description: "Complete bookkeeping and financial record management",
      icon: FileText
    },
    {
      title: "Tax Compliance",
      description: "Ensure full compliance with Polish tax regulations",
      icon: Shield
    },
    {
      title: "Financial Reporting",
      description: "Professional financial reports and analysis",
      icon: BarChart3
    },
    {
      title: "Cost Management",
      description: "Optimize your business costs and expenses",
      icon: TrendingUp
    }
  ];

  const services = [
    {
      title: "Daily Bookkeeping",
      description: "Complete daily financial record keeping and transaction management",
      icon: Calculator,
      features: [
        "Transaction recording and categorization",
        "Invoice management and tracking",
        "Expense monitoring and control",
        "Bank reconciliation services"
      ]
    },
    {
      title: "Tax Preparation",
      description: "Professional tax preparation and filing services",
      icon: Receipt,
      features: [
        "VAT returns and submissions",
        "Income tax preparation",
        "Corporate tax compliance",
        "Tax optimization strategies"
      ]
    },
    {
      title: "Financial Analysis",
      description: "Comprehensive financial analysis and reporting",
      icon: PieChart,
      features: [
        "Monthly and quarterly reports",
        "Profit and loss statements",
        "Cash flow analysis",
        "Budget planning and forecasting"
      ]
    },
    {
      title: "Payroll Management",
      description: "Complete payroll processing and management",
      icon: Users,
      features: [
        "Employee salary processing",
        "Social security contributions",
        "Tax deductions and calculations",
        "Payslip generation and distribution"
      ]
    }
  ];

  const benefits = [
    {
      title: "Expert Knowledge",
      description: "Our certified accountants understand Polish tax laws and regulations",
      icon: Award
    },
    {
      title: "Time Saving",
      description: "Focus on your business while we handle all accounting tasks",
      icon: Clock
    },
    {
      title: "Cost Effective",
      description: "Reduce overhead costs with our professional services",
      icon: DollarSign
    },
    {
      title: "Compliance Assurance",
      description: "Stay compliant with all Polish business regulations",
      icon: Shield
    }
  ];

  const processSteps = [
    {
      step: "1",
      title: "Initial Consultation",
      description: "We assess your business needs and current financial situation",
      icon: Users
    },
    {
      step: "2",
      title: "Setup & Integration",
      description: "Set up accounting systems and integrate with your business processes",
      icon: Building2
    },
    {
      step: "3",
      title: "Ongoing Management",
      description: "Daily bookkeeping, monthly reporting, and continuous support",
      icon: Calculator
    },
    {
      step: "4",
      title: "Tax Compliance",
      description: "Ensure timely tax filings and regulatory compliance",
      icon: Shield
    }
  ];

  const handleGetStarted = () => {
    if (!user) {
      navigate('/register');
      return;
    }
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sigma-grey via-white to-sigma-grey">
      <Header />
      
      {/* Hero Section */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-100 text-emerald-800 border-emerald-200 animate-fade-in">
              <Calculator className="w-4 h-4 mr-2" />
              Professional Bookkeeping Services
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-alumni font-bold text-sigma-black mb-6 animate-fade-in">
              Expert Bookkeeping Services for Your Business
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 max-w-4xl mx-auto font-hind animate-fade-in" style={{ animationDelay: '200ms' }}>
              Professional bookkeeping and accounting services to keep your business finances organized, compliant, and optimized for growth in Poland.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in" style={{ animationDelay: '400ms' }}>
            <Button 
              size="lg" 
              className="bg-sigma-red hover:bg-sigma-red/90 text-white px-8 py-4 text-lg font-semibold"
              onClick={handleGetStarted}
            >
              Get Started Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-sigma-red text-sigma-red hover:bg-sigma-red hover:text-white px-8 py-4 text-lg font-semibold"
              onClick={() => navigate('/consultation')}
            >
              Free Consultation
            </Button>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* What you get Section */}
      <div className="mb-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-4 font-alumni">What you get</h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
            Professional accounting services tailored for businesses in Poland
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <Card key={index} className="text-center border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                <CardHeader className="pb-4">
                  <div className="mx-auto bg-emerald-500/10 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                    <IconComponent className="h-6 w-6 text-emerald-600" />
                  </div>
                  <CardTitle className="text-lg font-bold text-foreground font-alumni">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-muted-foreground font-hind">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

        {/* Services Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 font-alumni">Our Bookkeeping Services</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
              Comprehensive accounting solutions for your business needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card 
                key={service.title}
                className="p-6 hover:shadow-lg transition-all duration-300 border-0 bg-white"
              >
                <CardHeader className="p-0 mb-4">
                  <div className="flex items-center mb-3">
                    <div className="bg-emerald-100 w-12 h-12 rounded-lg flex items-center justify-center mr-4">
                      <service.icon className="w-6 h-6 text-emerald-600" />
                    </div>
                    <CardTitle className="text-xl font-bold text-foreground font-alumni">{service.title}</CardTitle>
                  </div>
                  <CardDescription className="text-muted-foreground font-hind">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-muted-foreground font-hind">
                        <CheckCircle className="w-4 h-4 text-emerald-600 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

      {/* Process Section */}
      <div className="mb-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-4 font-alumni">Our Bookkeeping Process</h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
            Simple, transparent process to get your bookkeeping started
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {processSteps.map((step, index) => (
            <div key={step.step} className="text-center">
              <div className="bg-emerald-500/10 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-emerald-600">{step.step}</span>
              </div>
              <h3 className="text-lg font-bold text-foreground mb-2 font-alumni">{step.title}</h3>
              <p className="text-muted-foreground text-sm font-hind">{step.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Key Benefits Section */}
      <div className="mb-20">
        <div className="bg-gradient-to-br from-emerald-500/5 to-emerald-600/10 rounded-2xl p-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 font-alumni">Key Benefits</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
              Why professional bookkeeping is essential for your business success
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <span className="text-foreground font-hind">{benefit.description}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-2xl p-12 text-white">
            <h2 className="text-3xl font-bold mb-4 font-alumni">Ready to Get Your Bookkeeping Organized?</h2>
            <p className="text-xl mb-8 opacity-90 font-hind">
              Let our professional accountants handle your business finances while you focus on growth
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-emerald-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
                onClick={handleGetStarted}
              >
                Start Your Bookkeeping Service
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white !text-emerald-400 hover:bg-white/10 hover:!text-white px-8 py-4 text-lg font-semibold"
                onClick={() => navigate('/consultation')}
              >
                Schedule Consultation
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BookKeeping;
